import { useEffect } from 'react';
import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { useUser } from '@clerk/clerk-react';

// Get PostHog config from environment variables
const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

// Only initialize PostHog if env vars are set
const isPostHogEnabled = !!POSTHOG_KEY;

if (isPostHogEnabled) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: false, // We'll manually control this after identifying the user
    loaded: (posthog) => {
      if (import.meta.env.DEV) {
        // In development, you can see debug logs
        posthog.debug();
      }
    },
  });
}

function PostHogIdentifier() {
  const { user, isLoaded: isUserLoaded } = useUser();

  useEffect(() => {
    // Wait until user data is loaded
    if (!isPostHogEnabled || !isUserLoaded) return;

    if (user) {
      // Identify the user with only their ID, name, and email
      const userProperties = {
        email: user.primaryEmailAddress?.emailAddress,
        name: user.fullName || user.firstName || user.username,
      };

      // Identify user in PostHog
      posthog.identify(user.id, userProperties);

      // Now capture the initial pageview after identifying
      posthog.capture('$pageview');
    } else {
      // User logged out, reset PostHog
      posthog.reset();
    }
  }, [user, isUserLoaded]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  // If PostHog is not configured, just render children without the provider
  if (!isPostHogEnabled) {
    return <>{children}</>;
  }

  return (
    <PHProvider client={posthog}>
      <PostHogIdentifier />
      {children}
    </PHProvider>
  );
}